import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut } from 'lucide-react';

const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";

/**
 * Shared confirmation modal that gates the "Log Out" action across
 * every profile page (guest / host / venue / staff / admin). Rendered
 * inline (not via portal) so it inherits the app-shell dark-mode CSS
 * variables. Callers manage the `open` boolean.
 */
export default function LogoutConfirmModal({ open, onConfirm, onCancel }) {
  // Escape key = cancel; matches native browser dialogs.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="logout-confirm-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onCancel}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(6, 4, 20, 0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 9999,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 8 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 320,
              background: 'var(--surface)',
              border: '1px solid var(--card-border)',
              borderRadius: 24,
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)',
              padding: '26px 22px 18px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'var(--accent-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 4,
              }}
            >
              <LogOut size={26} color="var(--accent-purple)" strokeWidth={2} />
            </div>
            <h3
              style={{
                margin: 0,
                fontFamily: FONT,
                fontSize: 20,
                fontWeight: 700,
                color: 'var(--text)',
                textAlign: 'center',
              }}
            >
              Log out of Allure?
            </h3>
            <p
              style={{
                margin: '4px 0 18px',
                fontFamily: SUB_FONT,
                fontSize: 13.5,
                color: 'var(--text-2)',
                textAlign: 'center',
                lineHeight: 1.45,
              }}
            >
              You&apos;ll need to sign back in to keep using your account.
            </p>
            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <button
                type="button"
                data-testid="logout-confirm-cancel"
                onClick={onCancel}
                style={{
                  flex: 1,
                  padding: '12px 0',
                  border: '1px solid var(--card-border)',
                  borderRadius: 14,
                  background: 'var(--surface-2, var(--surface))',
                  color: 'var(--text)',
                  fontFamily: FONT,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                No
              </button>
              <button
                type="button"
                data-testid="logout-confirm-yes"
                onClick={onConfirm}
                style={{
                  flex: 1,
                  padding: '12px 0',
                  border: 'none',
                  borderRadius: 14,
                  background: 'var(--accent-purple)',
                  color: '#FFFFFF',
                  fontFamily: FONT,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: 'var(--premium-shadow-main)',
                }}
              >
                Yes, log out
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
